// src/components/SurveyList.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGeo } from '../context/GeoContext';
import { useAnonConsent } from '../context/AnonConsentContext';
import { apiGet, apiPost, bootstrapBffSession } from '../lib/apiClient';
import { getRecaptchaToken } from '../lib/recaptcha';
import { loadAccessCodeState, saveAccessCodeState } from '../lib/accessCodeStorage';
import '../styles/HomePage.css';
import '../styles/SurveyList.css';
import '../styles/SurveyForm.css';
import PostForm from './PostForm';
import ReplyThread from './ReplyThread';
import AnalyticsPanel from './AnalyticsPanel';
import AccessCodeModal from './AccessCodeModal';

// CHART_COLORS and Recharts usage moved into AnalyticsPanel

const METERS_TO_FEET = 3.28084;
const formatFeetInt = (ft) => new Intl.NumberFormat().format(Math.round(ft));
const MAX_CODE_ATTEMPTS = 3;

/** Precise client-side distance in meters (Haversine). */
function haversineMeters(lat1, lon1, lat2, lon2) {
  if (
    !Number.isFinite(lat1) || !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) || !Number.isFinite(lon2)
  ) return NaN;

  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function distanceTextFromMeters(meters) {
  if (!Number.isFinite(meters) || meters < 0) return 'Distance unknown';
  const feet = meters * METERS_TO_FEET;
  if (feet < 1) return '< 1 ft away';
  return `${formatFeetInt(feet)} ft away`;
}

const formatDateTimeDisplay = (value) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** Normalize a questions payload into [{id, question_text, question_type, options}] */
function normalizeQuestions(raw) {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw.items) ? raw.items : [];
  return arr.map((q) => {
    let options = q.options;
    if (typeof options === 'string') {
      try { options = JSON.parse(options); } catch { /* ignore */ }
    }
    return {
      id: q.id ?? q.question_id ?? q.qid ?? Math.random().toString(36).slice(2),
      question_text: q.question_text ?? q.text ?? q.title ?? '',
      question_type: q.question_type ?? q.type ?? 'free_text',
      options: Array.isArray(options) ? options : [],
      allow_multiple: !!(q.allow_multiple || q.allowMultiple),
      is_challenge: Boolean(q.is_challenge || q.isChallenge),
    };
  });
}

/** Fetch survey questions — single canonical endpoint */
async function fetchSurveyQuestions(surveyId, code) {
  const codeParam = code ? `?code=${encodeURIComponent(code)}` : '';
  const res = await apiGet(`/api/surveys/${surveyId}/questions${codeParam}`);
  if (typeof res === 'string' && res.startsWith('<!DOCTYPE')) {
    throw new Error('HTML error document');
  }
  const items = normalizeQuestions(res);
  return {
    items,
    expired: typeof res?.expired === 'boolean' ? Boolean(res.expired) : undefined,
  };
}

/** Fetch analytics rollup (precomputed) for a survey. */
async function fetchSurveyAnalytics(surveyId, code) {
  const codeParam = code ? `?code=${encodeURIComponent(code)}` : '';
  const path = `/api/surveys/${surveyId}/analytics${codeParam}`;
  const res = await apiGet(path);
  if (typeof res === 'string' && res.startsWith('<!DOCTYPE')) {
    throw new Error('HTML error document');
  }
  return res;
}

/** ---------- FLATTEN + ENRICH ANALYTICS ----------
 * Ensures a stable shape:
 * {
 *   resp_count: number,
 *   resp_count_timeseries: [{bucket, count}],
 *   mc_breakdown: { [qid]: { [option]: count } },
 *   question_totals: { [qid]: totalCountAcrossOptions },
 *   questions_map: { [qid]: question_text },           // <-- added
 *   sentiment_avg: number|null  (-1..1),
 *   sentiment_hist: { [bucketLabel]: count },
 *   top_keywords: [{term, count}],
 *   llm_summary: string,
 *   updated_at: ISO string
 * }
 */
function normalizeAnalyticsPayload(input) {
  if (!input) return null;

  // If server already returns a flat shape, make sure arrays/objects are in a predictable format
  if (!input.stats) {
    const out = { ...input };

    // keywords -> array
    if (out.top_keywords && !Array.isArray(out.top_keywords) && typeof out.top_keywords === 'object') {
      out.top_keywords = Object.entries(out.top_keywords).map(([term, count]) => ({ term, count: Number(count || 0) }));
    } else if (Array.isArray(out.top_keywords)) {
      out.top_keywords = out.top_keywords.map(k => ({ term: k.term ?? String(k[0]), count: Number(k.count ?? k[1] ?? 0) }));
    } else {
      out.top_keywords = [];
    }

    // sentiment
    if (out.sentiment_hist && typeof out.sentiment_hist === 'object') {
      const cleaned = {};
      for (const [k, v] of Object.entries(out.sentiment_hist)) cleaned[String(k)] = Number(v || 0);
      out.sentiment_hist = cleaned;
    } else {
      out.sentiment_hist = {};
    }

    // mc_breakdown normalize numbers
    if (out.mc_breakdown && typeof out.mc_breakdown === 'object') {
      const mb = {};
      for (const [qid, optMap] of Object.entries(out.mc_breakdown)) {
        mb[String(qid)] = {};
        for (const [opt, c] of Object.entries(optMap || {})) {
          mb[String(qid)][String(opt)] = Number(c || 0);
        }
      }
      out.mc_breakdown = mb;
    } else {
      out.mc_breakdown = {};
    }

    // Build per-question totals if missing
    if (!out.question_totals) {
      const totals = {};
      for (const [qid, optMap] of Object.entries(out.mc_breakdown)) {
        totals[qid] = Object.values(optMap).reduce((a, b) => a + Number(b || 0), 0);
      }
      out.question_totals = totals;
    }

    // Build questions_map from either provided map or stats-like array if present
    let qmap = {};
    if (out.questions_map && typeof out.questions_map === 'object') {
      for (const [k, v] of Object.entries(out.questions_map)) qmap[String(k)] = String(v || '');
    }
    // Some backends might inline questions array even without stats
    if (Array.isArray(out.questions)) {
      for (const q of out.questions) {
        const qid = String(q?.question_id ?? q?.id ?? '');
        const qtext = q?.question_text ?? '';
        if (qid && qtext) qmap[qid] = qtext;
      }
    }
    out.questions_map = qmap;

    // counts + series
    out.resp_count = Number(out.resp_count || 0);
    out.resp_count_timeseries = Array.isArray(out.resp_count_timeseries)
      ? out.resp_count_timeseries.map(d => ({ bucket: d.bucket, count: Number(d.count || 0) }))
      : [];

    out.sentiment_avg = (out.sentiment_avg === null || out.sentiment_avg === undefined)
      ? null
      : Number(out.sentiment_avg);

    out.llm_summary = out.llm_summary || '';
    out.updated_at = out.refreshed_at || out.updated_at || null;

    return out;
  }

  // Flatten from { stats: {...} }
  const flat = {};
  const stats = input.stats || {};

  flat.resp_count = Number(stats.totals?.responses ?? input.resp_count ?? 0);
  flat.resp_count_timeseries = (stats.timeseries || input.resp_count_timeseries || []).map(d => ({
    bucket: d.bucket, count: Number(d.count || 0)
  }));

  // MC breakdown + per-question totals
  const mb = {};
  const totals = {};
  const qmapFromStats = {};
  (stats.questions || []).forEach((q) => {
    const qid = String(q?.question_id ?? q?.id ?? '');
    if (!qid) return;

    // collect question text if available
    const qtext = q?.question_text ?? q?.text ?? '';
    if (qtext) qmapFromStats[qid] = qtext;

    if (q?.type === 'multiple_choice' && Array.isArray(q.breakdown)) {
      mb[qid] = {};
      let t = 0;
      q.breakdown.forEach((row) => {
        if (!row) return;
        const opt = String(row.option);
        const cnt = Number(row.count || 0);
        mb[qid][opt] = cnt;
        t += cnt;
      });
      totals[qid] = t;
    }
  });
  flat.mc_breakdown = Object.keys(mb).length ? mb : (input.mc_breakdown || {});
  if (!Object.keys(flat.mc_breakdown).length && input.mc_breakdown) {
    // If only legacy shape exists
    const t = {};
    for (const [qid, optMap] of Object.entries(input.mc_breakdown)) {
      t[qid] = Object.values(optMap).reduce((a, b) => a + Number(b || 0), 0);
    }
    flat.question_totals = t;
  } else {
    flat.question_totals = totals;
  }

  // Questions map: prefer explicit map if provided; otherwise use stats-derived
  let qmap = {};
  const maybeQs = (input?.stats?.questions) || [];   // when backend sends questions array
  if (Array.isArray(maybeQs)) {
    for (const q of maybeQs) {
      const qid = String(q?.question_id ?? q?.id ?? '');
      if (!qid) continue;
      const qtext = q?.question_text ?? '';
      if (qtext) qmap[qid] = qtext;
    }
  }
  if (input.questions_map && typeof input.questions_map === 'object') {
    for (const [k, v] of Object.entries(input.questions_map)) qmap[String(k)] = String(v || '');
  }
  flat.questions_map = { ...qmapFromStats, ...qmap };

  // Sentiment
  flat.sentiment_avg = (stats.sentiment?.avg ?? input.sentiment_avg ?? null);
  flat.sentiment_avg = (flat.sentiment_avg === null || flat.sentiment_avg === undefined)
    ? null
    : Number(flat.sentiment_avg);
  const sh = stats.sentiment?.hist || input.sentiment_hist || {};
  const cleanedHist = {};
  for (const [k, v] of Object.entries(sh)) cleanedHist[String(k)] = Number(v || 0);
  flat.sentiment_hist = cleanedHist;

  // Keywords
  let kws = stats.keywords || input.top_keywords || {};
  if (kws && !Array.isArray(kws) && typeof kws === 'object') {
    kws = Object.entries(kws).map(([term, count]) => ({ term, count: Number(count || 0) }));
  }
  flat.top_keywords = Array.isArray(kws)
    ? kws.map(k => ({ term: k.term ?? String(k[0]), count: Number(k.count ?? k[1] ?? 0) }))
    : [];

  // LLM + updated time
  flat.llm_summary = input.llm?.summary ?? input.llm_summary ?? '';
  flat.updated_at = input.refreshed_at || input.updated_at || null;

  return flat;
}

// AnalyticsPanel moved to its own file

export default function SurveyList({ intentQuery = '', maxRadiusFt, itemsOverride = null }) {
  const { coords } = useGeo();
  const { consented, loading: consentLoading } = useAnonConsent();
  const [params] = useSearchParams();
  const codeFromUrl = params.get('code') || '';

  const userLat = coords?.lat ?? null;
  const userLng = coords?.lng ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [respCount, setRespCount] = useState({});
  const [serviceAreaError, setServiceAreaError] = useState(null);

  const [likeCounts, setLikeCounts] = useState({});
  const [likeBusy, setLikeBusy] = useState({});
  const [likedLocal, setLikedLocal] = useState({});

  const [expanded, setExpanded] = useState(() => new Set());
  const [expandedAnalytics, setExpandedAnalytics] = useState(() => new Set());
  const [expandedDiscuss, setExpandedDiscuss] = useState(() => new Set());
  const [threadPostBySurvey, setThreadPostBySurvey] = useState({});
  const [postsBySurvey, setPostsBySurvey] = useState({});
  const [postsLoading, setPostsLoading] = useState({});
  const [postsError, setPostsError] = useState({});
  const [expandedReplies, setExpandedReplies] = useState(() => new Set());
  const [postLikeBusy, setPostLikeBusy] = useState({});
  const [postLikedLocal, setPostLikedLocal] = useState({});

  const [questionsBySurvey, setQuestionsBySurvey] = useState({});
  const [answersBySurvey, setAnswersBySurvey] = useState({});
  const [savingBySurvey, setSavingBySurvey] = useState({});
  const [respondedBySurvey, setRespondedBySurvey] = useState({});
  // Inline moderation/validation errors for survey responses (per survey)
  const [respErrorBySurvey, setRespErrorBySurvey] = useState({});
  const storageStateRef = useRef({ enabled: true });
  const [accessCodes, setAccessCodesState] = useState(() => {
    const { map, enabled } = loadAccessCodeState();
    storageStateRef.current.enabled = enabled;
    return map;
  });
  const [exportCodes, setExportCodes] = useState({});
  const [exportCodeModal, setExportCodeModal] = useState({
    surveyId: null,
    surveyUuid: null,
    open: false,
    error: '',
    busy: false,
    restricted: false,
  });
  const [codeMetaBySurvey, setCodeMetaBySurvey] = useState({});
  const [activeCodeSurveyId, setActiveCodeSurveyId] = useState(null);
  const [codeModalBusy, setCodeModalBusy] = useState(false);

  const [analyticsBySurvey, setAnalyticsBySurvey] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState({});
  const [analyticsError, setAnalyticsError] = useState({});

  const [copiedHomepageFor, setCopiedHomepageFor] = useState(null);
  const [homepageLinksBySurvey, setHomepageLinksBySurvey] = useState({});
  const [copiedShareFor, setCopiedShareFor] = useState(null);
  const [reportedLocal, setReportedLocal] = useState({});
  const [shareLinksBySurvey, setShareLinksBySurvey] = useState({});

  const consentWarning = 'Enable anonymous ID participation via the ID Policy page to interact.';

  const updateAccessCodes = useCallback((updater) => {
    setAccessCodesState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (storageStateRef.current.enabled) {
        const ok = saveAccessCodeState(next);
        if (!ok) storageStateRef.current.enabled = false;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (consented) {
      setError(null);
      setRespErrorBySurvey({});
    }
  }, [consented]);

  useEffect(() => {
    const { map, enabled } = loadAccessCodeState();
    storageStateRef.current.enabled = enabled;
    setAccessCodesState((prev) => {
      const next = { ...map };
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && prevKeys.every((key) => prev[key] === next[key])) {
        return prev;
      }
      return next;
    });
  }, [itemsOverride]);

  // Load surveys
  useEffect(() => {
    if (itemsOverride) {
      setSurveys(itemsOverride);
      setLoading(false);
      setError(null);
      setServiceAreaError(null);
      return;
    }
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      setServiceAreaError(null);

      try {
        const qs = new URLSearchParams({
          ...(Number.isFinite(userLat) && Number.isFinite(userLng) ? { lat: String(userLat), lng: String(userLng) } : {}),
        }).toString();

        const res = await apiGet(`/api/surveys${qs ? `?${qs}` : ''}`);
        if (!alive) return;

        const raw = Array.isArray(res?.items) ? res.items : [];

        const normalized = raw.map((s) => {
          const id = s.surveyId ?? s.id ?? s.survey_id ?? s.surveyid;
          const uuid = s.surveyUuid ?? s.uuid ?? s.survey_uuid ?? null;
          const createdAt = s.surveyCreatedAt ?? s.created_at ?? s.createdAt;
          const latNorm = Number.isFinite(s.latitude)
            ? s.latitude
            : Number.isFinite(s.center_lat) ? s.center_lat : null;
          const lonNorm = Number.isFinite(s.longitude)
            ? s.longitude
            : Number.isFinite(s.center_lon) ? s.center_lon : null;

          return {
            ...s,
            surveyId: id,
            surveyUuid: uuid,
            surveyCreatedAt: createdAt,
            latitude: latNorm,
            longitude: lonNorm,
            expired: typeof s.expired === 'boolean' ? Boolean(s.expired) : false,
            exportHtmlRestricted: Boolean(
              s.exportHtmlRestricted ??
              s.export_html_restricted ??
              s.requires_export_html_code ??
              s.export_html_code_set
            ),
          };
        });

        setSurveys(normalized);
        const counts = res?.counts && typeof res.counts === 'object' ? res.counts : {};
        setRespCount(counts);

        const lc = {};
        (normalized || []).forEach((s) => {
          lc[s.surveyId] = Number.isFinite(s.like_count) ? s.like_count : 0;
        });
        setLikeCounts(lc);
      } catch (e) {
        if (!alive) return;
        const msg = e?.msg || e?.message || String(e);
        setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (!itemsOverride) load();
    return () => { alive = false; };
  }, [userLat, userLng, codeFromUrl, itemsOverride]);

  // Did I respond?
  useEffect(() => {
    let alive = true;
    if (!surveys.length) return;
    (async () => {
      const ids = Array.from(new Set(surveys.map((s) => s.surveyId))).join(',');
      try {
        const { responded } = await apiGet(`/api/surveys/me/survey-responses?ids=${ids}`);
        if (alive) setRespondedBySurvey(responded || {});
      } catch (e) {
        console.error('fetch responded map error:', e?.message || e);
      }
    })();
    return () => { alive = false; };
  }, [surveys]);

  const ensureQuestionsLoaded = useCallback(async (surveyId, { code, countAttempt = false } = {}) => {
    try {
      const have = questionsBySurvey[surveyId];
      if (!countAttempt && have && Array.isArray(have) && have.length > 0) {
        return true;
      }
      const fallbackCode = (code ?? accessCodes[surveyId] ?? codeFromUrl ?? '').trim();
      const { items, expired } = await fetchSurveyQuestions(surveyId, fallbackCode);
      setQuestionsBySurvey((prev) => ({ ...prev, [surveyId]: items || [] }));
      setAnswersBySurvey((prev) => ({ ...prev, [surveyId]: prev[surveyId] || {} }));
      if (typeof expired === 'boolean') {
        setSurveys((prev) => prev.map((entry) => (
          entry.surveyId === surveyId ? { ...entry, expired } : entry
        )));
      }
      if (fallbackCode) {
        updateAccessCodes((prev) => ({ ...prev, [surveyId]: fallbackCode }));
      }
      setCodeMetaBySurvey((prev) => ({
        ...prev,
        [surveyId]: { attempts: 0, locked: false, requires: false, message: '' },
      }));
      return true;
    } catch (e) {
      if (e?.status === 403) {
        setQuestionsBySurvey((prev) => ({ ...prev, [surveyId]: null }));
        setCodeMetaBySurvey((prev) => {
          const prevEntry = prev[surveyId] || { attempts: 0, locked: false, requires: true, message: '' };
          const nextAttempts = countAttempt ? prevEntry.attempts + 1 : 0;
          const locked = countAttempt && nextAttempts >= MAX_CODE_ATTEMPTS;
          const message = locked
            ? 'Too many incorrect attempts. Access has been temporarily blocked.'
            : (e?.msg || e?.message || 'Access code required for this survey.');
          return {
            ...prev,
            [surveyId]: {
              attempts: countAttempt ? Math.min(nextAttempts, MAX_CODE_ATTEMPTS) : 0,
              locked,
              requires: true,
              message,
            },
          };
        });
        updateAccessCodes((prev) => {
          if (!prev[surveyId]) return prev;
          const next = { ...prev };
          delete next[surveyId];
          return next;
        });
        setActiveCodeSurveyId(surveyId);
        return false;
      }
      console.error(`questions fetch error for ${surveyId}:`, e?.message || e);
      setQuestionsBySurvey((prev) => ({ ...prev, [surveyId]: [] }));
      setCodeMetaBySurvey((prev) => ({
        ...prev,
        [surveyId]: {
          attempts: 0,
          locked: false,
          requires: false,
          message: e?.msg || e?.message || 'Failed to load questions.',
        },
      }));
      return false;
    }
  }, [questionsBySurvey, accessCodes, codeFromUrl, updateAccessCodes]);

  const toggleExpand = useCallback((surveyId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(surveyId)) {
        next.delete(surveyId);
      } else {
        next.add(surveyId);
        ensureQuestionsLoaded(surveyId, { code: accessCodes[surveyId] || codeFromUrl || '', countAttempt: false });
      }
      return next;
    });
  }, [ensureQuestionsLoaded, accessCodes, codeFromUrl]);

  const toggleAnalytics = useCallback((surveyId) => {
    setExpandedAnalytics((prev) => {
      const next = new Set(prev);
      if (next.has(surveyId)) next.delete(surveyId);
      else {
        next.add(surveyId);
        ensureQuestionsLoaded(surveyId, { code: accessCodes[surveyId] || codeFromUrl || '', countAttempt: false });
      }
      return next;
    });
  }, [ensureQuestionsLoaded, accessCodes, codeFromUrl]);

  const toggleDiscuss = useCallback((surveyId) => {
    setExpandedDiscuss((prev) => {
      const next = new Set(prev);
      if (next.has(surveyId)) next.delete(surveyId);
      else {
        next.add(surveyId);
        ensureQuestionsLoaded(surveyId, { code: accessCodes[surveyId] || codeFromUrl || '', countAttempt: false });
      }
      return next;
    });
  }, [ensureQuestionsLoaded, accessCodes, codeFromUrl]);

  // Prevent repeated loads when toggling Discuss; load once per survey id
  const loadedPostsRef = useRef(new Set());
  useEffect(() => {
    let alive = true;
    async function loadPosts(id) {
      try {
        setPostsLoading((p) => ({ ...p, [id]: true }));
        setPostsError((p) => ({ ...p, [id]: null }));
        // FIX: use backend-supported endpoint + filter
        const res = await apiGet(`/api/posts?surveyId=${id}`);
        if (!alive) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        setPostsBySurvey((p) => ({ ...p, [id]: items }));
      } catch (e) {
        if (!alive) return;
        setPostsError((p) => ({ ...p, [id]: e?.message || String(e) }));
      } finally {
        setPostsLoading((p) => ({ ...p, [id]: false }));
      }
    }
    expandedDiscuss.forEach((id) => {
      if (!loadedPostsRef.current.has(id)) {
        loadedPostsRef.current.add(id);
        loadPosts(id);
      }
    });
    return () => { alive = false; };
  }, [expandedDiscuss]);

  useEffect(() => {
    let alive = true;
    async function load(id) {
      try {
        setAnalyticsLoading((p) => ({ ...p, [id]: true }));
        setAnalyticsError((p) => ({ ...p, [id]: null }));

        const code = accessCodes[id] || codeFromUrl || '';
        const raw = await fetchSurveyAnalytics(id, code);
        const data = normalizeAnalyticsPayload(raw);

        setAnalyticsBySurvey((p) => ({ ...p, [id]: data }));
      } catch (e) {
        if (!alive) return;
        if (e?.status === 403) {
          setCodeMetaBySurvey((prev) => ({
            ...prev,
            [id]: {
              attempts: prev[id]?.attempts || 0,
              locked: prev[id]?.locked || false,
              requires: true,
              message: e?.msg || e?.message || 'Access code required for this survey.',
            },
          }));
          setActiveCodeSurveyId(id);
        } else {
          setAnalyticsError((p) => ({ ...p, [id]: e?.message || String(e) }));
        }
      } finally {
        setAnalyticsLoading((p) => ({ ...p, [id]: false }));
      }
    }
    expandedAnalytics.forEach((id) => {
      if (!analyticsBySurvey[id] && !analyticsLoading[id]) {
        load(id);
      }
    });
    return () => { alive = false; };
  }, [expandedAnalytics, analyticsBySurvey, analyticsLoading, accessCodes, codeFromUrl]);

  const handleAnswerChange = useCallback((surveyId, questionId, value) => {
    setAnswersBySurvey((prev) => ({
      ...prev,
      [surveyId]: { ...(prev[surveyId] || {}), [questionId]: value },
    }));
    // Clear any previous moderation/validation error once user edits
    setRespErrorBySurvey((prev) => (prev[surveyId] ? { ...prev, [surveyId]: null } : prev));
  }, []);

  const toggleMulti = useCallback((surveyId, questionId, option, checked) => {
    setAnswersBySurvey((prev) => {
      const prevSurvey = prev[surveyId] || {};
      const current = Array.isArray(prevSurvey[questionId]) ? prevSurvey[questionId] : [];
      let next;
      if (checked) {
        next = current.includes(option) ? current : [...current, option];
      } else {
        next = current.filter((v) => v !== option);
      }
      return { ...prev, [surveyId]: { ...prevSurvey, [questionId]: next } };
    });
    setRespErrorBySurvey((prev) => (prev[surveyId] ? { ...prev, [surveyId]: null } : prev));
  }, []);

  const promptForCode = useCallback((surveyId) => {
    if (!surveyId) return;
    setActiveCodeSurveyId(surveyId);
    setCodeModalBusy(false);
  }, []);

  const handleCodeModalSubmit = useCallback(async (value) => {
    if (!activeCodeSurveyId) return false;
    const entry = codeMetaBySurvey[activeCodeSurveyId];
    if (entry?.locked) return false;
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      setCodeMetaBySurvey((prev) => ({
        ...prev,
        [activeCodeSurveyId]: {
          ...(prev[activeCodeSurveyId] || { attempts: 0, locked: false, requires: true, message: '' }),
          requires: true,
          message: 'Please enter the access code.',
        },
      }));
      return false;
    }
    setCodeModalBusy(true);
    const ok = await ensureQuestionsLoaded(activeCodeSurveyId, { code: trimmed, countAttempt: true });
    setCodeModalBusy(false);
    if (ok) {
      updateAccessCodes((prev) => ({ ...prev, [activeCodeSurveyId]: trimmed }));
      setActiveCodeSurveyId(null);
    }
    return ok;
  }, [activeCodeSurveyId, codeMetaBySurvey, ensureQuestionsLoaded, updateAccessCodes]);

  const handleCodeModalClose = useCallback(() => {
    if (activeCodeSurveyId == null) return;
    const entry = codeMetaBySurvey[activeCodeSurveyId];
    if (entry?.locked) return;
    setActiveCodeSurveyId(null);
  }, [activeCodeSurveyId, codeMetaBySurvey]);

  const handleSubmit = useCallback(
    async (surveyId) => {
      if (respondedBySurvey[surveyId]) return;
      if (!consented) {
        setRespErrorBySurvey((prev) => ({ ...prev, [surveyId]: consentWarning }));
        return;
      }
      const meta = surveys.find((entry) => entry.surveyId === surveyId);
      if (meta?.expired) {
        setRespErrorBySurvey((prev) => ({ ...prev, [surveyId]: 'This survey is no longer accepting responses.' }));
        return;
      }

      const qs = questionsBySurvey[surveyId] || [];
      const answersMap = answersBySurvey[surveyId] || {};
      const answersObj = {};
      const challengeObj = {};
      const challengeCount = qs.reduce((total, q) => total + (q && (q.is_challenge || q.isChallenge) ? 1 : 0), 0);
      let missingChallenge = false;

      for (const q of qs) {
        if (!q) continue;
        const v = answersMap[q.id];
        const isChallenge = Boolean(q.is_challenge || q.isChallenge);

        if (isChallenge) {
          let normalized = '';
          if (Array.isArray(v)) {
            const firstFilled = v.find((entry) => String(entry || '').trim() !== '');
            normalized = firstFilled == null ? '' : String(firstFilled).trim();
          } else if (v != null) {
            normalized = String(v).trim();
          }
          if (normalized) {
            challengeObj[q.id] = normalized;
          } else {
            missingChallenge = true;
          }
          continue;
        }

        if (Array.isArray(v)) {
          if (v.length > 0) answersObj[q.id] = v;
        } else if (v != null) {
          const s = String(v).trim();
          if (s !== '') answersObj[q.id] = s;
        }
      }

      if (Object.keys(answersObj).length === 0) return;
      if (challengeCount > 0 && (missingChallenge || Object.keys(challengeObj).length < challengeCount)) {
        setRespErrorBySurvey((prev) => ({
          ...prev,
          [surveyId]: 'Please answer the challenge questions before submitting.',
        }));
        return;
      }

      setSavingBySurvey((prev) => ({ ...prev, [surveyId]: true }));

      try {
        const lat = Number.isFinite(coords?.lat) ? coords.lat : null;
        const lng = Number.isFinite(coords?.lng) ? coords.lng : null;
        const payload = { answers: answersObj, lat, lng };
        if (Object.keys(challengeObj).length > 0) {
          payload.challenge_answers = challengeObj;
        }
        const code = accessCodes[surveyId];
        if (code) payload.code = code;

        let recaptchaToken = null;
        try {
          recaptchaToken = await getRecaptchaToken('survey_response');
        } catch (err) {
          const message = err?.message || 'reCAPTCHA verification failed. Please try again.';
          setRespErrorBySurvey((prev) => ({ ...prev, [surveyId]: message }));
          return;
        }

        const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
        const resp = await apiPost(`/api/surveys/${surveyId}/responses`, payload, options);

        setRespondedBySurvey((prev) => ({ ...prev, [surveyId]: true }));

        if (!resp?.duplicate) {
          setRespCount((prev) => ({ ...prev, [surveyId]: (prev[surveyId] || 0) + 1 }));
        }

        setAnswersBySurvey((prev) => ({ ...prev, [surveyId]: {} }));
      } catch (e) {
        const originalMsg = e?.msg || e?.message || '';
        const msg = originalMsg.toLowerCase();
        if (msg.includes('already responded') || msg.includes('409')) {
          setRespondedBySurvey((prev) => ({ ...prev, [surveyId]: true }));
        } else {
          // Inline moderation error (HTTP 422 from server)
          if (e && e.status === 410) {
            const closure = e?.msg || e?.message || 'This survey is no longer accepting responses.';
            setRespErrorBySurvey((prev) => ({ ...prev, [surveyId]: closure }));
            const expiresValue = e?.expiresAt || (e?.data && e.data.expiresAt);
            setSurveys((prev) => prev.map((entry) => (
              entry.surveyId === surveyId
                ? { ...entry, expired: true, expiresAt: expiresValue || entry.expiresAt }
                : entry
            )));
          } else if (e && e.status === 422) {
            const m = e?.msg || 'Content not allowed.';
            setRespErrorBySurvey((prev) => ({ ...prev, [surveyId]: m }));
          } else if (e && e.status === 403) {
            const m = originalMsg || 'Participation disabled while anonymous ID is opted out.';
            setRespErrorBySurvey((prev) => ({ ...prev, [surveyId]: m }));
          } else {
            console.error('submit error:', e?.msg || e?.message || e);
            setError(e?.msg || e?.message || String(e));
          }
        }
      } finally {
        setSavingBySurvey((prev) => ({ ...prev, [surveyId]: false }));
      }
    },
    [respondedBySurvey, answersBySurvey, questionsBySurvey, coords?.lat, coords?.lng, consented, consentWarning, accessCodes, surveys]
  );

  const likeSurvey = useCallback(async (surveyId) => {
      if (!surveyId) return;
      if (likeBusy[surveyId]) return;
      if (!consented) {
        alert(consentWarning);
        return;
      }

    setLikeBusy((p) => ({ ...p, [surveyId]: true }));

    setLikedLocal((p) => ({ ...p, [surveyId]: true }));
    setLikeCounts((p) => ({ ...p, [surveyId]: (p[surveyId] || 0) + 1 }));

    try {
      await bootstrapBffSession();
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_like');
      } catch (err) {
        throw err instanceof Error ? err : new Error('reCAPTCHA verification failed. Please retry.');
      }
      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const data = await apiPost(`/api/surveys/${surveyId}/like`, {}, options);
      if (typeof data?.like_count === 'number') {
        setLikeCounts((p) => ({ ...p, [surveyId]: data.like_count }));
      }
    } catch (e) {
      setLikedLocal((p) => ({ ...p, [surveyId]: false }));
      setLikeCounts((p) => ({ ...p, [surveyId]: Math.max(0, (p[surveyId] || 1) - 1) }));
      const message = e?.msg || e?.message || e;
      console.error('like error:', message);
      if (typeof message === 'string' && message.toLowerCase().includes('recaptcha')) {
        alert(message);
      }
    } finally {
      setLikeBusy((p) => ({ ...p, [surveyId]: false }));
    }
  }, [likeBusy, consented, consentWarning]);


  const ensureShareToken = useCallback(async (surveyId) => {
    if (!surveyId) return null;
    const cached = shareLinksBySurvey[surveyId];
    if (cached?.token) return cached.token;
    try {
      await bootstrapBffSession();
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_share_link');
      } catch (err) {
        throw err instanceof Error ? err : new Error('reCAPTCHA verification failed. Please retry.');
      }
      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const data = await apiPost(`/api/surveys/${surveyId}/share-links`, {}, options);
      if (data?.token) {
        setShareLinksBySurvey((prev) => ({ ...prev, [surveyId]: data }));
        return data.token;
      }
    } catch (err) {
      const message = err?.message || err;
      console.error('create share link failed', message);
      if (typeof message === 'string' && message.toLowerCase().includes('recaptcha')) {
        alert(message);
      }
    }
    return cached?.token || null;
  }, [shareLinksBySurvey]);

  const ensureHomepageFocusLink = useCallback(async (s) => {
    if (!s?.surveyId) return null;
    const cached = homepageLinksBySurvey[s.surveyId];
    if (cached) return cached;
    try {
      await bootstrapBffSession();
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_share_link');
      } catch (err) {
        throw err instanceof Error ? err : new Error('reCAPTCHA verification failed. Please retry.');
      }
      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const params = new URLSearchParams({ utm_source: 'homepage' }).toString();
      const basePath = s.surveyUuid
        ? `/api/surveys/uuid/${encodeURIComponent(s.surveyUuid)}/links`
        : `/api/surveys/${s.surveyId}/links`;
      const data = await apiPost(`${basePath}?${params}`, {}, options);
      let focusUrl = data?.homepage_focus_url;
      if (!focusUrl) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const identifier = s.surveyUuid || s.surveyId;
        if (origin && identifier) {
          const delimiter = origin.endsWith('/') ? '' : '/';
          focusUrl = `${origin}${delimiter}?s=${encodeURIComponent(identifier)}&utm_source=homepage`;
        }
      }
      if (focusUrl) {
        setHomepageLinksBySurvey((prev) => ({ ...prev, [s.surveyId]: focusUrl }));
        return focusUrl;
      }
    } catch (err) {
      const message = err?.message || err;
      console.error('create homepage link failed', message);
      if (typeof message === 'string' && message.toLowerCase().includes('recaptcha')) {
        alert(message);
      }
    }
    return cached || null;
  }, [homepageLinksBySurvey]);

  const buildShareUrl = useCallback((s, token) => {
    try {
      const origin = window?.location?.origin || '';
      if (!origin) return '';
      const identifier = s?.surveyUuid || s?.surveyId;
      if (!identifier) return '';
      const params = new URLSearchParams();
      if (token) params.set('share_token', token);
      const query = params.toString();
      return `${origin}/surveys/${encodeURIComponent(identifier)}${query ? `?${query}` : ''}`;
    } catch {
      return '';
    }
  }, []);

  const copyHomepageLink = useCallback(async (s) => {
    const url = await ensureHomepageFocusLink(s);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedHomepageFor(s.surveyId);
      setTimeout(() => setCopiedHomepageFor((prev) => (prev === s.surveyId ? null : prev)), 1500);
    } catch (e) {
      console.warn('copy homepage link failed', e);
    }
  }, [ensureHomepageFocusLink]);

  const copyShare = useCallback(async (s) => {
    const token = await ensureShareToken(s.surveyId);
    const url = buildShareUrl(s, token);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedShareFor(s.surveyId);
      setTimeout(() => setCopiedShareFor((prev) => (prev === s.surveyId ? null : prev)), 1500);
    } catch (e) {
      console.warn('copy failed', e);
    }
  }, [buildShareUrl, ensureShareToken]);

  const reportSurvey = useCallback(async (surveyId) => {
    if (!surveyId) return;
    if (!consented) {
      setError(consentWarning);
      return;
    }
    const reason = window.prompt(
      'Once this survey receives enough unique removal reports from different users, it will be taken down.\n\nPlease share a brief reason for reporting it (no personal data):'
    );
    if (reason == null) return; // cancelled
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      alert('Please provide a brief reason (at least 3 characters).');
      return;
    }
    try {
      await bootstrapBffSession();
      const body = { reason: trimmed };
      const code = accessCodes[surveyId];
      if (code) body.code = code;
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_report');
      } catch (err) {
        throw err instanceof Error ? err : new Error('reCAPTCHA verification failed. Please retry.');
      }
      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      await apiPost(`/surveys/${surveyId}/report`, body, options);
      setReportedLocal((p) => ({ ...p, [surveyId]: true }));
      // Immediately remove from list for a cleaner UX; server also hides removed items
      setSurveys((arr) => arr.filter((s) => s.surveyId !== surveyId));
    } catch (e) {
      alert(e?.msg || e?.message || 'Failed to report survey');
    }
  }, [consented, consentWarning, accessCodes]);

  const activeSurveyTitle = useMemo(() => {
    if (!activeCodeSurveyId) return '';
    const match = surveys.find((s) => s.surveyId === activeCodeSurveyId);
    return match?.title || '';
  }, [activeCodeSurveyId, surveys]);

  const activeCodeMeta = activeCodeSurveyId ? codeMetaBySurvey[activeCodeSurveyId] : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const performExportHtml = useCallback(async ({ surveyId, surveyUuid, codeValue = '', fromModal = false }) => {
    if ((!surveyId && !surveyUuid) || typeof window === 'undefined') return false;
    const base = window.location.origin || window.location.href;
    const exportPath = surveyUuid
      ? `/api/surveys/uuid/${encodeURIComponent(surveyUuid)}/export/html`
      : `/api/surveys/${surveyId}/export/html`;
    const urlObj = new URL(exportPath, base);
    if (codeValue) {
      urlObj.searchParams.set('export_code', codeValue);
    }

    try {
      const token = await getRecaptchaToken('analytics_export');
      if (token) {
        urlObj.searchParams.set('recaptchaToken', token);
      }
    } catch (err) {
      console.error('reCAPTCHA export error:', err?.message || err);
      alert(err?.message || 'reCAPTCHA verification failed. Please retry.');
      if (previewWindow) previewWindow.close();
      return false;
    }

    try {
      const resp = await fetch(urlObj.toString(), { credentials: 'include' });
      if (!resp.ok) {
        let message = `Export failed (status ${resp.status}).`;
        try {
          const data = await resp.json();
          if (data?.message) message = data.message;
        } catch (jsonErr) {
          // ignore parse errors
        }
        if (resp.status === 403 && message.toLowerCase().includes('export code required')) {
          setExportCodeModal({
            surveyId,
            surveyUuid,
            open: true,
            error: '',
            busy: false,
            restricted: true,
          });
          return false;
        }
        alert(message);
        return false;
      }

      const html = await resp.text();
      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `survey-${surveyId}-export.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

      if (codeValue) {
        setExportCodes((prev) => ({ ...prev, [surveyId]: codeValue }));
      }
      if (fromModal) {
        setExportCodeModal({ surveyId: null, surveyUuid: null, open: false, error: '', busy: false, restricted: false });
      }
      return true;
    } catch (err) {
      if (previewWindow) previewWindow.close();
      console.error('export html failed:', err?.message || err);
      alert(err?.message || 'Failed to export HTML.');
      return false;
    }
  }, []);
  const codeModalElement = (
    <AccessCodeModal
      open={activeCodeSurveyId != null}
      busy={codeModalBusy}
      locked={Boolean(activeCodeMeta?.locked)}
      attempts={activeCodeMeta?.attempts || 0}
      maxAttempts={MAX_CODE_ATTEMPTS}
      error={activeCodeMeta?.message || ''}
      title={activeSurveyTitle ? `Access code for "${activeSurveyTitle}"` : 'Enter Access Code'}
      description="Enter the access code to view and respond to this survey."
      onSubmit={handleCodeModalSubmit}
      onCancel={activeCodeMeta?.locked ? undefined : handleCodeModalClose}
    />
  );

  const exportCodeModalElement = (
    <AccessCodeModal
      open={exportCodeModal.open}
      busy={exportCodeModal.busy}
      locked={false}
      attempts={0}
      maxAttempts={MAX_CODE_ATTEMPTS}
      error={exportCodeModal.error}
      title="Export HTML Access Code"
      description={exportCodeModal.restricted
        ? 'This survey owner requires a code before HTML exports are allowed.'
        : 'Enter a code to continue exporting this survey.'}
      onSubmit={async (value) => {
        const trimmed = (value || '').trim();
        if (trimmed.length < 4) {
          setExportCodeModal((prev) => ({ ...prev, error: 'Code must be at least 4 characters.' }));
          return false;
        }
        if (/\s/.test(trimmed)) {
          setExportCodeModal((prev) => ({ ...prev, error: 'Code cannot contain spaces.' }));
          return false;
        }
        const surveyId = exportCodeModal.surveyId;
        const surveyUuid = exportCodeModal.surveyUuid;
        if (!surveyId && !surveyUuid) return false;
        setExportCodeModal((prev) => ({ ...prev, busy: true, error: '' }));
        const success = await performExportHtml({
          surveyId: surveyId || null,
          surveyUuid: surveyUuid || null,
          codeValue: trimmed,
          fromModal: true,
        });
        if (!success) {
          setExportCodeModal((prev) => ({ ...prev, busy: false }));
        }
        return success;
      }}
      onCancel={() => setExportCodeModal({ surveyId: null, surveyUuid: null, open: false, error: '', busy: false, restricted: false })}
    />
  );

  const renderSummary = useCallback(
    (s) => {
      const responses = respCount[s.surveyId] ?? s.responses_count ?? 0;
      const comments = s.post_count ?? 0;
      const likes = likeCounts[s.surveyId] ?? s.like_count ?? 0;
      const iLiked = !!likedLocal[s.surveyId];
      const busy = !!likeBusy[s.surveyId];

      const analyticsOpen = expandedAnalytics.has(s.surveyId);
      const discussOpen = expandedDiscuss.has(s.surveyId);
      const expiresValue = s.expiresAt;
      const expiresLabel = expiresValue ? formatDateTimeDisplay(expiresValue) : '';
      const lifecycleLabel = expiresLabel ? `${s.expired ? 'Closed' : 'Closes'} ${expiresLabel}` : '';

      return (
        <div className="survey-card">
          <button
            type="button"
            onClick={() => toggleExpand(s.surveyId)}
            className="survey-card__tap"
            aria-label={`Open survey ${s.title}`}
          >
            <div className="survey-card-title">
              {s.title}{' '}
              {s.expired && (
                <span className="badge" style={{ marginLeft: 6, background: '#fee2e2', color: '#991b1b' }}>
                  Closed
                </span>
              )}
              {respondedBySurvey[s.surveyId] && (
                <span className="badge" style={{ marginLeft: 6 }}>Responded</span>
              )}
            </div>
            {s.instructions && <div className="survey-card-sub">{s.instructions}</div>}
            <div className="survey-card-meta">
              {new Date(s.surveyCreatedAt).toLocaleString()}
              {s._distanceText !== 'Distance unknown' && ` · ${s._distanceText}`}
              · Responses: {responses}
              · Comments: {comments}
              {lifecycleLabel && ` · ${lifecycleLabel}`}
            </div>
          </button>

          <div className="survey-card-actions">
            <button
              type="button"
              className="icon-button"
              onClick={(e) => { e.stopPropagation(); copyShare(s); }}
              title="Copy share link"
              aria-label="Copy survey link"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M7 7h7a4 4 0 0 1 4 4v7a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-7a4 4 0 0 1 4-4Zm10-4H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={(e) => { e.stopPropagation(); copyHomepageLink(s); }}
              title="Copy homepage link"
              aria-label="Copy homepage link"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M10.59 13.41a4 4 0 0 1 0-5.66l1.83-1.83a4 4 0 1 1 5.66 5.66l-1.06 1.06" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M13.41 10.59a4 4 0 0 1 0 5.66l-1.83 1.83a4 4 0 0 1-5.66-5.66l1.06-1.06" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              className={`icon-button ${analyticsOpen ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleAnalytics(s.surveyId); }}
              title={analyticsOpen ? 'Hide Insights' : 'Show Insights'}
              aria-label="Insights / Analytics"
              aria-expanded={analyticsOpen}
              aria-controls={`analytics-${s.surveyId}`}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M4 19h16M6 17V9m6 8V5m6 14v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <button
              type="button"
              className={`icon-button ${discussOpen ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleDiscuss(s.surveyId); }}
              title={discussOpen ? 'Hide Discuss' : 'Respond / Discuss'}
              aria-label="Respond / Discuss"
              aria-expanded={discussOpen}
              aria-controls={`discuss-${s.surveyId}`}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M21 12a8.5 8.5 0 0 1-1.4 4.7c-.3.5-.5.8-.5 1.3 0 .4.2.8.5 1.1l.6.6c.2.2.2.5 0 .7a.5.5 0 0 1-.4.1c-1.9-.4-3.4-1-4.4-1.6a9.5 9.5 0 1 1 5.6-8.9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button
              type="button"
              className={`like-button ${iLiked ? 'liked' : ''}`}
              disabled={busy || iLiked}
              onClick={(e) => { e.stopPropagation(); likeSurvey(s.surveyId); }}
              aria-pressed={iLiked}
              aria-label="Like survey"
              title={iLiked ? 'You liked this' : 'Like this survey'}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M2 21h4V9H2v12Zm6 0h9.2a3 3 0 0 0 2.95-2.51l1.13-7.2A3 3 0 0 0 18.31 8H14V5a3 3 0 0 0-3-3h-.5l-1 4L7 9v12Z" fill={iLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" />
              </svg>
              <span className="like-count">{likes}</span>
            </button>

            <button
              type="button"
              className="icon-button"
              onClick={(e) => { e.stopPropagation(); reportSurvey(s.surveyId); }}
              title="Report survey for removal"
              aria-label="Report survey"
              disabled={!!reportedLocal[s.surveyId]}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M12 2l9 4-9 4-9-4 9-4Zm0 8l9 4-9 4-9-4 9-4Z" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>
          {copiedHomepageFor === s.surveyId && <span className="badge" style={{ marginTop: 6 }}>Copied</span>}
          {copiedShareFor === s.surveyId && <span className="badge" style={{ marginTop: 6 }}>Copied!</span>}
          {!!reportedLocal[s.surveyId] && <span className="badge" style={{ marginTop: 6, background:'#fee2e2', color:'#991b1b' }}>Reported</span>}
        </div>
      );
    },
    [
      respCount, likeCounts, likedLocal, likeBusy,
      toggleExpand, respondedBySurvey, likeSurvey,
      expandedAnalytics, toggleAnalytics, expandedDiscuss, toggleDiscuss,
      copiedHomepageFor, copyHomepageLink,
      copiedShareFor, copyShare, reportSurvey, reportedLocal
    ]
  );

  const renderExpanded = useCallback(
    (s) => {
      const meta = codeMetaBySurvey[s.surveyId];
      if (meta?.requires) {
        return (
          <div className="card" style={{ marginTop: 8 }}>
            <div className="error" style={{ marginBottom: 8 }}>
              {meta.message || 'Access code required for this survey.'}
            </div>
            {meta.locked ? (
              <div className="muted">Access is blocked after too many incorrect attempts.</div>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={(e) => { e.stopPropagation(); promptForCode(s.surveyId); }}
              >
                Enter Access Code
              </button>
            )}
          </div>
        );
      }

      const qs = questionsBySurvey[s.surveyId];
      if (!qs) return <div className="card" style={{ marginTop: 8 }}>Loading questions…</div>;

      if (Array.isArray(qs) && qs.length === 0) {
        return (
          <div className="card" style={{ marginTop: 8 }}>
            <div className="error">No questions available for this survey.</div>
          </div>
        );
      }

      const answers = answersBySurvey[s.surveyId] || {};
      const saving = !!savingBySurvey[s.surveyId];
      const responded = !!respondedBySurvey[s.surveyId];
      const respErr = respErrorBySurvey[s.surveyId];
      const hasChallenge = Array.isArray(qs) && qs.some((q) => q && (q.is_challenge || q.isChallenge));
      const expired = Boolean(s.expired);
      const expiresAtLabel = s.expiresAt ? formatDateTimeDisplay(s.expiresAt) : '';

      return (
        <div className="card" style={{ marginTop: 8 }}>
          {responded && <div className="info" style={{ marginBottom: 8 }}>You’ve already submitted this survey. Thanks!</div>}
          {expired && (
            <div className="error" style={{ marginBottom: 12 }}>
              {expiresAtLabel
                ? `Responses closed on ${expiresAtLabel}.`
                : 'This survey is no longer accepting responses.'}
            </div>
          )}
          {!consented && !consentLoading && (
            <div className="muted" style={{ marginBottom: 12 }}>
              Read-only mode: enable anonymous ID participation on the <a href="/privacy/anonymous-id">ID Policy</a> page to submit responses.
            </div>
          )}

          {hasChallenge && (
            <div className="muted" style={{ marginBottom: 12 }}>
              Challenge questions must be answered correctly to submit. They help confirm the responder knows the topic.
              <a
                href="/guide#challenge-questions"
                style={{ marginLeft: 6 }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
              .
            </div>
          )}

          {qs.map((q) => {
            const qText = q.question_text || q.questionText;
            const qType = q.question_type || q.questionType;
            const isChallenge = Boolean(q.is_challenge || q.isChallenge);
            return (
            <div
              className="question-block"
              key={q.id}
              style={isChallenge ? { borderColor: '#f97316' } : undefined}
            >
              <div className="question-text" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isChallenge && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        background: '#f97316',
                        color: '#fff',
                        borderRadius: 4,
                        padding: '2px 6px',
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Challenge
                    </span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      Answer correctly to submit —
                      <a
                        href="/guide#challenge-questions"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: 4 }}
                      >
                        learn more
                      </a>
                    </span>
                  </div>
                )}
                <span>{qText}</span>
              </div>

              {qType === 'multiple_choice' && Array.isArray(q.options) && (
                <div className="options">
                  {q.options.map((opt, idx) => {
                    const isMulti = !!(q.allow_multiple || q.allowMultiple);
                    return (
                      <label key={idx} className="option-row">
                        {isMulti ? (
                          <input
                            type="checkbox"
                            name={`s-${s.surveyId}-q-${q.id}`}
                            value={opt}
                          checked={Array.isArray(answers[q.id]) && answers[q.id].includes(opt)}
                          onChange={(e) => toggleMulti(s.surveyId, q.id, opt, e.target.checked)}
                          disabled={responded || expired}
                        />
                      ) : (
                          <input
                            type="radio"
                            name={`s-${s.surveyId}-q-${q.id}`}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={(e) => handleAnswerChange(s.surveyId, q.id, e.target.value)}
                            disabled={responded || expired}
                          />
                        )}
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {qType === 'free_text' && (
                <textarea
                  className="free-text"
                  rows={3}
                  placeholder={isChallenge ? 'Enter the challenge answer' : 'Type your answer'}
                  value={answers[q.id] || ''}
                  onChange={(e) => handleAnswerChange(s.surveyId, q.id, e.target.value)}
                  disabled={responded || expired}
                  maxLength={240}
                />
              )}
            </div>
          );})}

          {respErr && (
            <div className="error" style={{ marginTop: 8 }}>
              {respErr}
            </div>
          )}

          <div className="actions">
            {consented ? (
              <button className="btn" disabled={saving || responded || !!respErr || expired} onClick={() => handleSubmit(s.surveyId)}>
                {expired ? 'Responses closed' : saving ? 'Submitting…' : responded ? 'Already submitted' : 'Submit Answers'}
              </button>
            ) : (
              <a className="btn" href="/privacy/anonymous-id" style={{ textDecoration: 'none' }}>
                Enable anonymous ID to submit
              </a>
            )}
          </div>

          {consented && saving && !responded && !expired && (
            <div
              className="progress-bar"
              aria-label="Submitting responses, please wait"
              role="status"
              style={{ marginTop: 12 }}
            >
              <div className="progress-bar-inner" />
            </div>
          )}
        </div>
      );
    },
    [
      codeMetaBySurvey,
      promptForCode,
      questionsBySurvey,
      answersBySurvey,
      savingBySurvey,
      respondedBySurvey,
      handleAnswerChange,
      handleSubmit,
      respErrorBySurvey,
      toggleMulti,
      consented,
      consentLoading
    ]
  );

  const renderAnalytics = useCallback((s) => {
    if (!expandedAnalytics.has(s.surveyId)) return null;
    const loading = !!analyticsLoading[s.surveyId];
    const analytics = analyticsBySurvey[s.surveyId];
    const err = analyticsError[s.surveyId];

    const refresh = async () => {
      try {
        setAnalyticsLoading((p) => ({ ...p, [s.surveyId]: true }));
        setAnalyticsError((p) => ({ ...p, [s.surveyId]: null }));
        const code = accessCodes[s.surveyId] || codeFromUrl || '';
        const raw = await fetchSurveyAnalytics(s.surveyId, code);
        const data = normalizeAnalyticsPayload(raw);
        setAnalyticsBySurvey((p) => ({ ...p, [s.surveyId]: data }));
      } catch (e) {
        setAnalyticsError((p) => ({ ...p, [s.surveyId]: e?.message || String(e) }));
      } finally {
        setAnalyticsLoading((p) => ({ ...p, [s.surveyId]: false }));
      }
    };

    const handleExportHtml = async () => {
      if (!s?.surveyId) return;
      if (typeof window === 'undefined') return;

      const existingCode = exportCodes[s.surveyId] || '';
      const requiresCode = Boolean(
        s.exportHtmlRestricted ||
        s.export_html_restricted ||
        s.requires_export_html_code
      );

      if (!requiresCode) {
        performExportHtml({ surveyId: s.surveyId, surveyUuid: s.surveyUuid, codeValue: existingCode });
        return;
      }

      if (existingCode) {
        const success = await performExportHtml({ surveyId: s.surveyId, surveyUuid: s.surveyUuid, codeValue: existingCode });
        if (success) {
          return;
        }
      }

      setExportCodeModal({
        surveyId: s.surveyId,
        surveyUuid: s.surveyUuid,
        open: true,
        error: '',
        busy: false,
        restricted: true,
      });
    };

    return (
      <div id={`analytics-${s.surveyId}`} aria-live="polite">
        <AnalyticsPanel
          surveyId={s.surveyId}
          analytics={analytics}
          loading={loading}
          error={err}
          onRefresh={refresh}
          onExportHtml={handleExportHtml}
          exportHtmlRestricted={Boolean(s.exportHtmlRestricted)}
        />
      </div>
    );
  }, [expandedAnalytics, analyticsLoading, analyticsBySurvey, analyticsError, accessCodes, codeFromUrl, exportCodes, performExportHtml]);

  const renderDiscuss = useCallback((s) => {
    if (!expandedDiscuss.has(s.surveyId)) return null;
    const plist = postsBySurvey[s.surveyId] || [];
    const pLoading = !!postsLoading[s.surveyId];
    const pErr = postsError[s.surveyId];

    const likePost = async (postId) => {
      if (!postId || postLikeBusy[postId] || postLikedLocal[postId]) return;
      setPostLikeBusy((p) => ({ ...p, [postId]: true }));
      try {
        await bootstrapBffSession();
        let recaptchaToken = null;
        try {
          recaptchaToken = await getRecaptchaToken('post_like');
        } catch (err) {
          throw err instanceof Error ? err : new Error('reCAPTCHA verification failed. Please retry.');
        }
        const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
        const data = await apiPost('/api/posts/like', { postId }, options);
        const nextCount = typeof data?.like_count === 'number' ? data.like_count : null;
        setPostLikedLocal((p) => ({ ...p, [postId]: true }));
        if (nextCount != null) {
          setPostsBySurvey((prev) => {
            const list = prev[s.surveyId] || [];
            return {
              ...prev,
              [s.surveyId]: list.map((p) => (p.id === postId ? { ...p, like_count: nextCount } : p)),
            };
          });
        }
      } catch (e) {
        const message = e?.message || e;
        console.warn('post like failed:', message);
        if (typeof message === 'string' && message.toLowerCase().includes('recaptcha')) {
          alert(message);
        }
      } finally {
        setPostLikeBusy((p) => ({ ...p, [postId]: false }));
      }
    };

    return (
      <div id={`discuss-${s.surveyId}`} className="card" style={{ marginTop: 8 }}>
        <h4 style={{ marginTop: 0 }}>Discuss</h4>
        <PostForm
          surveyId={s.surveyId}
          onPost={(created) => {
            if (created && created.id) {
              setThreadPostBySurvey((p) => ({ ...p, [s.surveyId]: created.id }));
              setPostsBySurvey((p) => ({
                ...p, [s.surveyId]: [{
                  id: created.id,
                  content: created.content,
                  user_id: created.user_id,
                  like_count: created.like_count ?? 0,
                  reply_count: created.reply_count ?? 0,
                  created_at: created.created_at,
                }, ...(p[s.surveyId] || [])]
              }));
            }
          }}
        />
        {pErr && <div className="error" style={{ marginTop: 8 }}>{pErr}</div>}
        {pLoading && <div className="muted" style={{ marginTop: 8 }}>Loading posts…</div>}
        {!pLoading && plist.length > 0 && (
          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
            {plist.map((post) => (
              <div key={post.id} className="card" style={{ padding: 10, textAlign: 'left' }}>
                <div style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}>{post.content}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{new Date(post.created_at).toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    <button
                      className={`like-button ${postLikedLocal[post.id] ? 'liked' : ''}`}
                      onClick={() => likePost(post.id)}
                      disabled={!!postLikedLocal[post.id] || !!postLikeBusy[post.id]}
                      title={postLikedLocal[post.id] ? 'You liked this' : 'Like'}
                      aria-pressed={!!postLikedLocal[post.id]}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                        <path d="M2 21h4V9H2v12Zm6 0h9.2a3 3 0 0 0 2.95-2.51l1.13-7.2A3 3 0 0 0 18.31 8H14V5a3 3 0 0 0-3-3h-.5l-1 4L7 9v12Z" fill={postLikedLocal[post.id] ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                      <span className="like-count">{post.like_count || 0}</span>
                    </button>
                    <button
                      className={`like-button ${expandedReplies.has(post.id) ? 'liked' : ''}`}
                      style={{ marginLeft: 6 }}
                      onClick={() => {
                        setExpandedReplies((prev) => {
                          const next = new Set(prev);
                          if (next.has(post.id)) next.delete(post.id); else next.add(post.id);
                          return next;
                        });
                      }}
                      aria-expanded={expandedReplies.has(post.id)}
                      aria-controls={`replies-${post.id}`}
                      title={expandedReplies.has(post.id) ? 'Hide replies' : 'Show replies'}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" style={{ verticalAlign: 'text-bottom' }}>
                        <path d="M21 12a8.5 8.5 0 0 1-1.4 4.7c-.3.5-.5.8-.5 1.3 0 .4.2.8.5 1.1l.6.6c.2.2.2.5 0 .7a.5.5 0 0 1-.4.1c-1.9-.4-3.4-1-4.4-1.6a9.5 9.5 0 1 1 5.6-8.9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="like-count">{post.reply_count || 0}</span>
                    </button>
                  </div>
                </div>
                {expandedReplies.has(post.id) && (
                  <div id={`replies-${post.id}`} style={{ marginTop: 8 }}>
                    <ReplyThread
                      postId={post.id}
                      onReplyCountChange={(count) => {
                        setPostsBySurvey((prev) => {
                          const list = prev[s.surveyId] || [];
                          let changed = false;
                          const nextList = list.map((p) => {
                            if (p.id !== post.id) return p;
                            if (p.reply_count === count) return p;
                            changed = true;
                            return { ...p, reply_count: count };
                          });
                          if (!changed) return prev;
                          return { ...prev, [s.surveyId]: nextList };
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }, [expandedDiscuss, postsBySurvey, postsLoading, postsError, expandedReplies, postLikeBusy, postLikedLocal]);

  const enrichedSurveys = useMemo(() => {
    const haveUser = Number.isFinite(userLat) && Number.isFinite(userLng);
    return (surveys || []).map((s) => {
      let meters = Number.isFinite(s.distanceMeters) ? s.distanceMeters : NaN;
      if (haveUser && Number.isFinite(s.latitude) && Number.isFinite(s.longitude)) {
        meters = haversineMeters(userLat, userLng, s.latitude, s.longitude);
      }
      return {
        ...s,
        _distanceMetersClient: meters,
        _distanceText: distanceTextFromMeters(meters),
      };
    });
  }, [surveys, userLat, userLng]);

  const filteredSurveys = useMemo(() => {
    const q = (intentQuery || '').trim().toLowerCase();
    if (!q) return enrichedSurveys;
    return (enrichedSurveys || []).filter((s) => {
      const t = String(s.title || '').toLowerCase();
      const i = String(s.instructions || '').toLowerCase();
      return t.includes(q) || i.includes(q);
    });
  }, [enrichedSurveys, intentQuery]);

  if (loading) return <>{codeModalElement}<div className="card">Loading surveys…</div></>;
  if (serviceAreaError) return <>{codeModalElement}<div className="card info">{serviceAreaError}</div></>;
  if (error) return <>{codeModalElement}<div className="card error">Error: {error}</div></>;
  if (!filteredSurveys.length) return <>{codeModalElement}<div className="card">No surveys found.</div></>;

  return (
    <>
      {codeModalElement}
      {exportCodeModalElement}
      <div className="list">
      {filteredSurveys.map((s) => (
        <div key={s.surveyId}>
          {renderSummary(s)}
          {expanded.has(s.surveyId) && renderExpanded(s)}
          {renderAnalytics(s)}
          {renderDiscuss(s)}
        </div>
      ))}
      </div>
    </>
  );
}
